<template>
<div class="kt-project-forecast" :class="{ 'is-embedded': embedded }" :style="c_borderStyle">
	<div class="pf-header" v-if="embedded">
		<b-form-input
			size="sm"
			class="pf-name-input"
			:value="project.name"
			@input="m_updateField('name', $event)"
		/>
	</div>

	<div class="pf-form">
		<div class="pf-field" v-if="!embedded">
			<label>Project Name</label>
			<b-form-input
				size="sm"
				:value="project.name"
				@input="m_updateField('name', $event)"
			/>
		</div>
		<div class="pf-field">
			<label>Contract Value</label>
			<b-input-group prepend="$" size="sm">
				<b-form-input
				type="text"
				:value="m_formatMoney(project.contractValue)"
				@input="m_updateField('contractValue', m_parseMoney($event))"
				/>
			</b-input-group>
		</div>
		<div class="pf-field">
			<label>Fee %</label>
			<b-input-group append="%" size="sm">
				<b-form-input
					type="number"
					min="0"
					max="100"
					step="0.1"
					:value="project.feePct"
					@input="m_updateField('feePct', $event)"
				/>
			</b-input-group>
		</div>
		<div class="pf-field">
			<label>Total Fee</label>
			<span class="pf-readonly-val pf-fee-total">{{ fmt(c_totalFeeValue) }}</span>
		</div>
		<div class="pf-field">
			<label>Duration (months)</label>
			<b-form-input
				type="number"
				min="2"
				max="120"
				size="sm"
				:value="project.durationMonths"
				@input="m_updateField('durationMonths', $event)"
			/>
		</div>
		<div class="pf-field" v-if="embedded">
			<label>Start Date</label>
			<span class="pf-readonly-val">{{ c_startDateLabel }}</span>
		</div>
		<div class="pf-field" v-if="embedded">
			<label>End Date</label>
			<span class="pf-readonly-val">{{ c_endDateLabel }}</span>
		</div>
		<div class="pf-field" v-if="!embedded">
			<label>Start Date</label>
			<b-form-input
				type="month"
				size="sm"
				:value="project.startDate"
				@input="m_updateField('startDate', $event)"
			/>
		</div>
		<div class="pf-field">
			<label>Curve Method</label>
			<b-form-select
				size="sm"
				:value="project.curveMethod"
				:options="c_curveOptions"
				@input="m_updateField('curveMethod', $event)"
			/>
		</div>
		<div class="pf-field">
			<label>S-Curve Steepness</label>
			<b-form-select
				size="sm"
				:value="project.sCurveSteepness"
				:options="c_steepnessOptions"
				:disabled="project.curveMethod !== 'scurve'"
				@input="m_updateField('sCurveSteepness', $event)"
			/>
		</div>
	</div>

	<template v-if="!embedded">
		<div class="pf-summary">
			<div class="pf-metric">
				<span class="pf-metric-lbl">Contract Value</span>
				<span class="pf-metric-val">{{ fmt(c_summary.contractValue) }}</span>
			</div>
			<div class="pf-metric">
				<span class="pf-metric-lbl">Duration</span>
				<span class="pf-metric-val">{{ c_summary.durationMonths }} mo</span>
			</div>
			<div class="pf-metric">
				<span class="pf-metric-lbl">Peak Monthly</span>
				<span class="pf-metric-val">{{ fmt(c_summary.peakMonthly) }}</span>
			</div>
			<div class="pf-metric">
				<span class="pf-metric-lbl">Avg Monthly</span>
				<span class="pf-metric-val">{{ fmt(c_summary.avgMonthly) }}</span>
			</div>
		</div>

		<div class="pf-chart-wrap">
			<canvas ref="chartCanvas" />
		</div>

		<div class="pf-table-wrap">
			<b-table-simple small hover striped class="pf-table">
				<b-thead>
					<b-tr>
						<b-th>Month</b-th>
						<b-th class="text-right">Revenue</b-th>
						<b-th class="text-right">% of Contract</b-th>
						<b-th class="text-right">Cumulative</b-th>
					</b-tr>
				</b-thead>
				<b-tbody>
					<b-tr v-for="row in c_schedule" :key="row.monthKey">
						<b-td>{{ row.monthLabel }}</b-td>
						<b-td class="text-right">{{ fmt(row.revenue) }}</b-td>
						<b-td class="text-right">{{ row.pctOfContract.toFixed(1) }}%</b-td>
						<b-td class="text-right">{{ fmt(row.cumulative) }}</b-td>
					</b-tr>
				</b-tbody>
			</b-table-simple>
		</div>
	</template>
</div>
</template>

<script>
import Chart from 'chart.js';
import { buildMonthlySchedule, fmt, fn_applyFeePct, fn_forecastEndDate, fn_forecastStartDateFull, CURVE_METHOD_OPTIONS, S_CURVE_STEEPNESS_OPTIONS } from '@/utils/forecastUtils.js';

export default {
	name: 'kt-project-forecast',
	props: {
		project: { type: Object, required: true },
		color: { type: String, default: null },
		embedded: { type: Boolean, default: false },
		retainagePct: { type: Number, default: 0 },
		paymentLagDays: { type: Number, default: 0 },
		grossMarginPct: { type: Number, default: 0 },
	},
	data() {
		return {
			d_chart: null,
			fmt,
		};
	},
	computed: {
		c_curveOptions() {
			return CURVE_METHOD_OPTIONS;
		},
		c_steepnessOptions() {
			return S_CURVE_STEEPNESS_OPTIONS;
		},
		c_schedule() {
			return buildMonthlySchedule(this.project);
		},
		c_summary() {
			const schedule = this.c_schedule;
			const revenues = schedule.map(r => r.revenue);
			const peakMonthly = revenues.length ? Math.max(...revenues) : 0;
			const avgMonthly = revenues.length
				? revenues.reduce((a, b) => a + b, 0) / revenues.length
				: 0;
			return {
				contractValue: Number(this.project.contractValue) || 0,
				durationMonths: Math.max(2, Math.min(120, Math.round(Number(this.project.durationMonths) || 2))),
				peakMonthly,
				avgMonthly,
			};
		},
		c_borderStyle() {
			if (!this.embedded || !this.color) return null;
			return { borderLeftColor: this.color };
		},
		c_startDateLabel() {
			return this.m_fmtDate(this.project.startDate);
		},
		c_endDateLabel() {
			return this.m_fmtDate(this.project.endDate);
		},
		c_totalFeeValue() {
			return fn_applyFeePct(Number(this.project.contractValue) || 0, this.project.feePct);
		},
	},
	watch: {
		c_schedule: {
			deep: true,
			handler() {
				if (!this.embedded) this.m_renderChart();
			},
		},
	},
	mounted() {
		if (!this.embedded) this.m_renderChart();
	},
	beforeDestroy() {
		this.m_destroyChart();
	},
	methods: {
		m_formatMoney(val) {
			if (val == null || val === '') return '';
				return Number(val).toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
				});
		},
		m_parseMoney(val) {
			if (!val) return 0;
			return Number(val.replace(/,/g, ''));
		},
		m_fmtDate(dt) {
			if (!dt) return '—';
			const d = new Date(dt + (dt.length <= 10 ? 'T12:00:00' : ''));
			if (isNaN(d)) return '—';
			const mm = String(d.getMonth() + 1).padStart(2, '0');
			const dd = String(d.getDate()).padStart(2, '0');
			return `${mm}-${dd}-${d.getFullYear()}`;
		},
		m_updateField(field, value) {
			const next = { ...this.project };
			if (field === 'contractValue' || field === 'durationMonths' || field === 'sCurveSteepness' || field === 'feePct') {
				next[field] = Number(value);
			} else {
				next[field] = value;
			}
			if (field === 'durationMonths') {
				next.durationMonths = Math.max(2, Math.min(120, Math.round(next.durationMonths) || 2));
			}
			if (field === 'feePct') {
				next.feePct = Math.max(0, Math.min(100, Number(value) || 0));
				let businessInsightRec={recordId:this.project.id,recordType:this.project.type,propName:'Fee',valueDEC:value,valueVNC:null};
				this.$emit('updateBusinessInsight',businessInsightRec);
			}
			if(['curveMethod','sCurveSteepness'].includes(field)) {
			     let businessInsightRec={recordId:this.project.id,recordType:this.project.type,propName:field,valueNVC:value, valueDEC:null};
				 this.$emit('updateBusinessInsight',businessInsightRec);
				 }
				 
			this.$emit('update:project', next);
		},
		m_destroyChart() {
			if (this.d_chart) {
				this.d_chart.destroy();
				this.d_chart = null;
			}
		},
		m_renderChart() {
			const canvas = this.$refs.chartCanvas;
			if (!canvas) return;
			this.m_destroyChart();
			const schedule = this.c_schedule;
			const labels = schedule.map(r => r.monthLabel);
			const ctx = canvas.getContext('2d');
			this.d_chart = new Chart(ctx, {
				type: 'bar',
				data: {
					labels,
					datasets: [
						{
							type: 'bar',
							label: 'Monthly Revenue',
							data: schedule.map(r => r.revenue),
							backgroundColor: this.color || '#185FA5',
							yAxisID: 'y-axis-0',
						},
						{
							type: 'line',
							label: 'Cumulative %',
							data: schedule.map(r => r.cumulativePct),
							borderColor: '#D85A30',
							backgroundColor: 'transparent',
							fill: false,
							lineTension: 0.2,
							pointRadius: 2,
							yAxisID: 'y-axis-1',
						},
					],
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					legend: { display: true, position: 'top' },
					tooltips: {
						callbacks: {
							label(tooltipItem, data) {
								const ds = data.datasets[tooltipItem.datasetIndex];
								if (ds.yAxisID === 'y-axis-1') {
									return ds.label + ': ' + tooltipItem.yLabel.toFixed(1) + '%';
								}
								return ds.label + ': ' + fmt(tooltipItem.yLabel);
							},
						},
					},
					scales: {
						xAxes: [{ gridLines: { display: false } }],
						yAxes: [
							{
								id: 'y-axis-0',
								position: 'left',
								ticks: {
									beginAtZero: true,
									callback: v => fmt(v),
								},
							},
							{
								id: 'y-axis-1',
								position: 'right',
								ticks: {
									min: 0,
									max: 100,
									callback: v => v + '%',
								},
								gridLines: { drawOnChartArea: false },
							},
						],
					},
				},
			});
		},
	},
};
</script>

<style lang="less" scoped>
.kt-project-forecast {
	&.is-embedded {
		border-left: 4px solid #185FA5;
		padding: 0.5em 0.75em;
		margin-bottom: 0.75em;
		background: #f8f9fa;
		border-radius: 0 0.25em 0.25em 0;
	}
}

.pf-header {
	display: flex;
	align-items: center;
	margin-bottom: 0.5em;
	.pf-name-input {
		flex: 1;
		font-weight: 600;
	}
}

.pf-form {
	display: flex;
	flex-wrap: wrap;
	gap: 0.5em 1em;
	margin-bottom: 0.75em;
}

.pf-field {
	flex: 1 1 140px;
	min-width: 120px;
	label {
		display: block;
		font-size: 0.75em;
		font-weight: 600;
		margin-bottom: 0.15em;
		color: #495057;
	}
}

.pf-readonly-val {
	display: block;
	font-size: 0.9em;
	padding: 0.25em 0;
	color: #212529;
}

.pf-fee-total {
	font-weight: 600;
}

.pf-summary {
	display: flex;
	flex-wrap: wrap;
	gap: 0.5em 1.5em;
	margin-bottom: 0.75em;
	padding: 0.5em 0.75em;
	background: #f8f9fa;
	border-radius: 0.25em;
}

.pf-metric {
	display: flex;
	flex-direction: column;
	.pf-metric-lbl {
		font-size: 0.75em;
		color: #6c757d;
	}
	.pf-metric-val {
		font-weight: 600;
		font-size: 0.95em;
	}
}

.pf-chart-wrap {
	position: relative;
	height: 280px;
	margin-bottom: 0.75em;
}

.pf-table-wrap {
	max-height: 240px;
	overflow-y: auto;
	overflow-x: auto;
}

.pf-table {
	font-size: 0.85em;
	margin-bottom: 0;
}
</style>
